import { APIBaseURL, CAPTIVE_PORTAL_DEFAULTS, CaptivePortal } from '@meterup/common';
import { useGraphQL } from '@meterup/graphql';
import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import type { GetCaptivePortalQuery as GetCaptivePortalQueryQuery } from '../gql/graphql';
import { graphql } from '../gql';

type RouteParams = {
  uuid: string;
};

const GetCaptivePortalQuery = graphql(`
  query GetCaptivePortal($uuid: UUID!) {
    captivePortalWithSignature(UUID: $uuid) {
      UUID
      callToAction
      displaySettings {
        backgroundColor
        fontColor
        callToActionColor
        hidePoweredByMeter
      }
      logoImageBase64 {
        data
        imageType
      }
      termsAndConditions
      requiresApproval
      requiresEmail
      requiresName
      useCompanyWebhook
    }
  }
`);

type QueryData = Omit<GetCaptivePortalQueryQuery['captivePortalWithSignature'], '__typename'>;
const dummyData: QueryData = {
  UUID: '',
  displaySettings: {
    backgroundColor: '#FFFFFF',
    fontColor: '#343647',
    callToActionColor: '#343647',
  },
  callToAction: `Connect to Meter's WiFi`,
  termsAndConditions: `Dolore legimus ius te, ea falli iriure vim, ad sea zril primis gubergren. Ea has quem graece persius, qui ut iisque latine. Mea at nostrud definiebas, atomorum similique ex per. Tale feugait molestiae ea has.

Ex eruditi alienum usu, at erant voluptua repudiare has. Ne pro alia explicari forensibus, wisi mundi doming mel ea. Autem percipit eam id, te eum expetenda definitionem. Sed saepe vocibus commune cu, mei no viderer theophrastus, ei mel affert definitiones.

Mundi suavitate ad cum. Sint placerat sed ex, has nisl animal aperiri ut, harum nominavi quo at. Ei dolorum habemus pri. Nam ad antiopam urbanitas dissentiunt, eos an atomorum vituperata referrentur.

Ex ius nihil ocurreret omittantur, congue adipiscing ne usu. Pro odio pertinax te, est vidit tritani menandri ei, in aliquid inimicus pri. No sed dicat adversarium neglegentur, in enim recteque argumentum cum, in tacimates salutatus persecuti sea. Affert iisque noluisse et eam, vis an quis doming option. Cu has nibh legimus appareat, quo et dicam vidisse vocibus.

Ea dicant mnesarchum definiebas nec, an nulla nihil maluisset his. Utroque nonumes te ius. Quas melius propriae ad per, et sanctus pericula ius, at mei alii minim. Labitur dignissim eos no.`,
  requiresApproval: false,
  requiresEmail: false,
  requiresName: false,
  useCompanyWebhook: false,
};

export default function Portal() {
  const { uuid } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const useDummyData = searchParams.get('use-dummy-data') === '1';
  const captivePortal = useGraphQL(
    GetCaptivePortalQuery,
    { uuid: uuid! },
    {
      enabled: !!uuid && !useDummyData,
    },
  );
  const data = useMemo<QueryData>(() => {
    if (useDummyData) {
      return {
        ...dummyData,
        UUID: uuid!,
      };
    }
    const { captivePortalWithSignature: capo } = captivePortal.data ?? {};
    if (capo) {
      return {
        ...capo,
        logoImageURL: CAPTIVE_PORTAL_DEFAULTS.logoImageURL,
        displaySettings: {
          backgroundColor:
            capo.displaySettings?.backgroundColor ??
            CAPTIVE_PORTAL_DEFAULTS.displaySettings.backgroundColor,
          fontColor:
            capo.displaySettings?.fontColor ?? CAPTIVE_PORTAL_DEFAULTS.displaySettings.fontColor,
          callToActionColor:
            capo.displaySettings?.callToActionColor ??
            CAPTIVE_PORTAL_DEFAULTS.displaySettings.callToActionColor,
          hidePoweredByMeter:
            capo.displaySettings?.hidePoweredByMeter ??
            CAPTIVE_PORTAL_DEFAULTS.displaySettings.hidePoweredByMeter,
        },
        requiresApproval: capo.requiresApproval,
        requiresEmail: capo.requiresEmail,
        requiresName: capo.requiresName,
        useCompanyWebhook: capo.useCompanyWebhook,
      };
    }
    return {
      ...CAPTIVE_PORTAL_DEFAULTS,
      UUID: uuid!,
    };
  }, [captivePortal.data, useDummyData, uuid]);

  const confirmLink = useMemo(() => {
    // /v2/captive-portals/:uuid/ack?signature=
    const url = new URL(`${APIBaseURL}/v2/captive-portals/${uuid}/ack`);
    url.search = new URLSearchParams({
      signature: searchParams.get('signature') ?? '',
      ip: searchParams.get('ip') ?? '',
      mac: searchParams.get('mac') ?? '',
      date: searchParams.get('date') ?? '',
    }).toString();
    return url.toString();
  }, [searchParams, uuid]);

  return <CaptivePortal relation="standalone" captivePortal={data} confirmLink={confirmLink} />;
}
